import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, Watch } from '@stencil/core';
import { isMagnoliaEditingMode } from '../../helper';
import { Trackable } from '../../types/global';

interface UiCmsTabGroupTabBody extends Trackable {
  variant: 'container' | 'default';
  centered?: boolean; // only in combination with default variant
  noLine?: boolean; // only in combination with default variant
}

@Component({
  tag: 'ui-cms-tab-group',
  styleUrl: 'ui-cms-tab-group.scss',
})
export class UiCmsTabGroup {
  @Element() el: HTMLUiCmsTabGroupElement;

  @Prop({ mutable: true }) body: UiCmsTabGroupTabBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop() inverted: boolean;

  private refTabGroup: HTMLElement;

  private refCmsTabs: HTMLUiCmsTabElement[];

  private observer: IntersectionObserver;

  @Event({ eventName: 'anchorCall' }) anchorCallEvent: EventEmitter<{ name: string }>;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Listen('hashchange', { target: 'window' })
  onHashChange(e: HashChangeEvent) {
    this.setActiveTabBasedOnUrl(new URL(e.newURL).hash);
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
  }

  componentDidLoad() {
    this.refCmsTabs = Array.from(this.el.querySelectorAll('ui-cms-tab'));

    // pass variant to 'sd-tab' elements
    this.refTabGroup.querySelectorAll('sd-tab').forEach((item) => item.setAttribute('variant', this.body.variant));

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.refCmsTabs = Array.from(this.el.querySelectorAll('ui-cms-tab'));
            // set first tab active if no tab is initially active
            if (this.refCmsTabs.length > 0 && !this.refCmsTabs.some((item) => item.body.active))
              this.refCmsTabs[0].body = { ...this.refCmsTabs[0].body, active: true };
            requestAnimationFrame(() => {
              this.removeCmsTabWrappers();
            });
            this.observer.disconnect();
          }
        });
      },
      { threshold: 0.03 }
    );

    this.observer.observe(this.el);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /**
   * We have to use this workaround to prepare the slotted content,
   * because we have an additional cms-wrapper for the sd-tab
   */
  private removeCmsTabWrappers(): void {
    // Preparing and moving children of 'ui-cms-tab' elements
    const flattenTabsContent = this.refCmsTabs.flatMap((item) => Array.from(item.children));
    flattenTabsContent.forEach((item) => this.refTabGroup.appendChild(item));

    // Remove 'ui-cms-tab' wrappers
    this.refCmsTabs.forEach((element) => element.remove());
  }

  private setActiveTabBasedOnUrl(hash: string) {
    if (hash) {
      this.updateActiveTab(hash.replace('#', ''));
    }
  }

  private updateActiveTab(newHash: string) {
    const anchorElems = [...this.el.querySelectorAll('ui-cms-anchor')].filter(
      (elem) => elem.closest('sd-tab') || elem.closest('sd-tab-panel')
    );

    const tabAnchor = anchorElems.find((anchor) => {
      if (anchor.id === newHash) return true;
      return false;
    });
    const selectedPanel = tabAnchor?.closest('sd-tab-panel');
    const selectedTabId = selectedPanel.id.replace('sd-tab-panel', 'sd-tab');

    const currentlyActivePanel = [...this.refTabGroup.querySelectorAll('sd-tab-panel')].find((item) =>
      item.hasAttribute('active')
    );
    const allTabs = [...this.refTabGroup.querySelectorAll('sd-tab')];
    const currentlyActiveTab = allTabs.find((item) => item.hasAttribute('active'));

    const tabToActivate = allTabs.find((item) => item.id === selectedTabId);
    if (tabToActivate) {
      currentlyActivePanel.removeAttribute('active');
      currentlyActiveTab.removeAttribute('active');
      tabToActivate.setAttribute('active', '');
      selectedPanel.setAttribute('active', '');
    }

    this.anchorCallEvent.emit({
      name: newHash,
    });
  }

  render() {
    return (
      <Host>
        {isMagnoliaEditingMode() ? (
          <slot></slot>
        ) : (
          <sd-tab-group
            class={{
              noLine: this.body.variant === 'default' && this.body.noLine,
              centered: this.body.variant === 'default' && this.body.centered,
              'sd-tab-group--inverted': this.inverted,
            }}
            activation="auto"
            ref={(el) => {
              this.refTabGroup = el;
            }}
          >
            <slot></slot>
          </sd-tab-group>
        )}
      </Host>
    );
  }
}
